import { Alert, Button, Flex, Input, Modal } from "antd";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import {
  usePostTriggerSMSVerification,
  usePostVerifySMSVerification,
} from "../../../api/api";
import { useGetProfile } from "../../../api/api.get";
import { useUpdatePhone } from "../../../api/api.update";
import { FormRowCol } from "../../../components/forms/form.row.component";
import { useMessage } from "../../../components/notifications/message";
import { useNotifications2 } from "../../../components/notifications/notification";
import { useAuthContext } from "../../../providers/auth.provider";
import {
  displayPhoneNumber,
  formatBlankWithDefault,
} from "../../../utils/functions";

export default function PhoneSection({ areaCode }: { areaCode: string }) {
  const { user: userAuth } = useAuthContext();
  const { data, refetch } = useGetProfile();
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (userAuth) {
      refetch();
    }
  }, [userAuth]);

  const user = data;

  const [isLoading, setIsLoading] = useState(false);
  const { notification, notifySuccess, notifyError } = useNotifications2();
  const {
    notification: messageNotification,
    messageSuccess,
    messageError,
  } = useMessage();

  const { mutateAsync: updatePhoneAsync } = useUpdatePhone();
  const [overwriteMode, setOverwriteMode] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: {
      phone: displayPhoneNumber(user?.phone, user?.countryCode),
      consent: false,
    },
    validationSchema: Yup.object({
      phone: Yup.string()
        .matches(/^[0-9]+$/, "Please enter only numbers.")
        .required("Please enter phone."),
      consent: Yup.bool().oneOf([true], "Please accept the consent."),
    }),
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      // @ts-ignore
      delete values["consent"];
      setIsLoading(true);

      updatePhoneAsync({ ...values, phone: `${areaCode}${values.phone}` })
        .then(() => {
          notifySuccess({
            title: "Success",
            description: "Phone number successfully updated.",
          });
          setIsEditMode(false);
        })
        .catch((err) => {
          if (err.response.status === 409) {
            setOverwriteMode(true);
          } else {
            notifyError("Whoops! Please try again");
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
  });

  return (
    <div className="my-10">
      {notification}
      {messageNotification}

      <OverwritePhoneModal
        areaCode={areaCode}
        phone={formik.values.phone}
        open={overwriteMode}
        onClose={() => setOverwriteMode(false)}
        onOK={() => {}}
      />

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Communications</h2>
          <p className="text-sm text-gray-600 mt-1">
            Your communication preferences
          </p>
        </div>

        <form style={{ paddingLeft: 30, paddingRight: 30, paddingBottom: 30 }}>
          <div>
            <FormRowCol label="Phone">
              <div>
                <div>
                  {isEditMode && (
                    <Input
                      type="text"
                      prefix={areaCode}
                      className="bordered"
                      maxLength={10}
                      {...formik.getFieldProps("phone")}
                    />
                  )}
                  {!isEditMode && (
                    <strong>
                      {formatBlankWithDefault(formik.values.phone, "-")}
                    </strong>
                  )}
                </div>
              </div>
              {formik.errors.phone && (
                <span style={{ fontSize: 15 }} className="errorMsg">
                  {formik.errors.phone}
                </span>
              )}

              {isEditMode && (
                <div className="divFlex my-4">
                  <p
                    style={{
                      fontSize: 15,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    className="mx-0-"
                  >
                    <input
                      id="consent-check"
                      type="checkbox"
                      checked={formik.values.consent}
                      style={{ marginRight: 10 }}
                      onChange={(e) =>
                        formik.setFieldValue("consent", e.target.checked)
                      }
                    />
                    By checking this box, you agree to receive text messages
                    from ExpenseHut and you are solely responsible for any
                    mobile carrier charges.
                  </p>
                </div>
              )}

              {formik.errors.consent && (
                <span style={{ fontSize: 15 }} className="errorMsg">
                  {formik.errors.consent}
                </span>
              )}
            </FormRowCol>
          </div>

          <div className="w-full flex justify-end">
            {isEditMode && (
              <Flex gap={30}>
                <Button type="link" onClick={() => setIsEditMode(false)}>
                  Cancel
                </Button>
                <Button
                  id="update-phone"
                  type="primary"
                  onClick={formik.submitForm}
                >
                  Save
                </Button>
              </Flex>
            )}
            {!isEditMode && (
              <Button
                type="link"
                id="edit-phone-btn"
                onClick={() => setIsEditMode(true)}
              >
                Edit Communication Info
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function OverwritePhoneModal({
  open,
  onClose,
  onOK,
  phone,
  areaCode,
}: {
  phone: string;
  areaCode: string;
  open: boolean;
  onClose: () => void;
  onOK: () => void;
}) {
  const [verifyMode, setVerifyMode] = useState<boolean>(false);
  const { messageSuccess, messageError, notification } = useMessage();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string>();

  const { mutateAsync, isLoading: isTriggerSMSLoading } =
    usePostTriggerSMSVerification();

  const {
    mutateAsync: verifyPasscodeAsync,
    isLoading: isVerifyPasscodeLoading,
  } = usePostVerifySMSVerification();

  useEffect(() => {
    setVerifyMode(false);
  }, [open]);

  const handleTriggerSMS = () => {
    setError(undefined);

    mutateAsync({ phone: `${areaCode}${phone}` })
      .then(() => {
        messageSuccess("One time passcode sent successfully.");
        setVerifyMode(true);
      })
      .catch((err) => {
        if (err.response.status === 403) {
          setError("You have exceeded the number of attempts");
        } else {
          setError("Whoops! Unable to perform request.");
        }
      });
  };

  const handleVerifySMS = () => {
    setError(undefined);
    verifyPasscodeAsync({ phone: `${areaCode}${phone}`, code: +code })
      .then(() => {
        messageSuccess("One time passcode verified and phone number updated.");
        setVerifyMode(true);
        onClose();
      })
      .catch((err) => {
        setError("Whoops! Invalid passcode. Please try again.");
      });
  };

  const handleClose = () => {
    setVerifyMode(false);
    onClose();
  };

  return (
    <Modal
      title="Phone number in use"
      open={open}
      onOk={onOK}
      onCancel={onClose}
      okText="Confirm"
      cancelText="Cancel"
      okButtonProps={{
        disabled: false,
        loading: false,
      }}
      footer={null}
    >
      {notification}
      <p>
        We found out this phone number is already in use. If this is yours,
        would you like to confirm it through an One Time Passcode (OTP)?
      </p>
      <p>
        <p>
          <Input prefix={areaCode} disabled={true} value={phone} />
        </p>

        {error && <Alert type="error" message={error} />}

        {!verifyMode && (
          <Flex>
            <Flex gap={5} className="my-40">
              <Button type="text" onClick={handleClose}>
                Cancel
              </Button>

              <Button
                loading={isTriggerSMSLoading}
                type="primary"
                onClick={handleTriggerSMS}
              >
                Send OTP
              </Button>
            </Flex>
          </Flex>
        )}

        {verifyMode && (
          <Flex vertical className="my-40">
            <Input
              placeholder="Enter OTP..."
              id="verify-otp-input"
              onChange={(e) => setCode(e.target.value)}
              value={code}
            />

            <Flex gap={5} className="my-40">
              <Button type="text" onClick={handleClose}>
                Cancel
              </Button>

              <Button
                loading={isVerifyPasscodeLoading}
                type="primary"
                disabled={!code}
                onClick={handleVerifySMS}
              >
                Verify
              </Button>
            </Flex>
          </Flex>
        )}
      </p>
    </Modal>
  );
}
